import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { COLORS } from '../../utils/constants';
import { Eye, X, Check, BookOpen } from 'lucide-react';
import api from '../../services/api';

const C = COLORS;

const ResultList = () => {
  const location = useLocation();
  const isPractice = location.pathname.includes('/practice');
  const type = isPractice ? 'Practice' : 'Tests';

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDetail, setViewDetail] = useState(null); // stores the detailed submission data
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchResults();
  }, [type]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/quizzes/submissions/type?type=${type}`);
      setResults(res.data);
    } catch (err) {
      console.error(err);
      alert('Failed to fetch results');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (submissionId) => {
    try {
      setDetailLoading(true);
      const res = await api.get(`/quizzes/submissions/${submissionId}`);
      setViewDetail(res.data);
    } catch (err) {
      console.error(err);
      alert('Failed to fetch submission details');
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <AppLayout title={`${type} Results`}>
      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `2px solid ${C.g200}` }}>
                <th style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>User</th>
                <th style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Quiz Name</th>
                <th style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Dates</th>
                <th style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Time to Complete</th>
                <th style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Score</th>
                <th style={{ padding: '16px 20px', fontSize: 13, fontWeight: 700, color: C.g600, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>Loading results...</td></tr>
              ) : results.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>No results found.</td></tr>
              ) : (
                results.map(r => {
                  const correct = r.score;
                  const wrong = r.total_questions - r.score;
                  return (
                    <tr key={r.submission_id} style={{ borderBottom: `1px solid ${C.g100}`, transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = C.g50} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: 600, color: C.g900 }}>{r.user_name || 'N/A'}</div>
                        <div style={{ fontSize: 13, color: C.g500 }}>{r.user_email || 'N/A'}</div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: 500, color: C.g800 }}>{r.quiz_name}</div>
                      </td>
                      <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                        <div><span style={{ color: C.g400 }}>Quiz:</span> {formatDate(r.quiz_date)}</div>
                        <div style={{ marginTop: 4 }}><span style={{ color: C.g400 }}>Attempt:</span> {formatDate(r.attempt_date)}</div>
                      </td>
                      <td style={{ padding: '16px 20px', fontSize: 14, color: C.g700 }}>
                        {r.time_taken !== undefined && r.time_taken !== null ? `${r.time_taken} min` : '-'}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#16A34A', background: '#DCFCE7', padding: '2px 8px', borderRadius: 12 }}>{correct} Correct</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#DC2626', background: '#FEE2E2', padding: '2px 8px', borderRadius: 12 }}>{wrong} Wrong</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <button
                          onClick={() => handleViewDetail(r.submission_id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: C.primaryGhost, color: C.primary, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
                          onMouseOver={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                          onMouseOut={e => { e.currentTarget.style.background = C.primaryGhost; e.currentTarget.style.color = C.primary; }}
                        >
                          <Eye size={16} /> View Detail
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Detail Modal */}
      {viewDetail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 700, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${C.g100}`, background: C.g50 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: C.g900, fontWeight: 700 }}>Submission Details</h3>
              <button onClick={() => setViewDetail(null)} style={{ background: 'none', border: 'none', color: C.g500, cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 6 }} onMouseOver={e => e.currentTarget.style.background = C.g200} onMouseOut={e => e.currentTarget.style.background = 'none'}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: 24, overflowY: 'auto', background: '#F9FAFB' }}>
              <div style={{ marginBottom: 20, display: 'flex', gap: 20, background: '#fff', padding: 16, borderRadius: 12, border: `1px solid ${C.g200}` }}>
                <div>
                  <div style={{ fontSize: 12, color: C.g500, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Score</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.primary }}>{viewDetail.score} / {viewDetail.total_questions}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: C.g500, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Subject</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: C.g800 }}>{viewDetail.subject_name || 'N/A'}</div>
                </div>
              </div>

              {viewDetail.answers && viewDetail.answers.map((ans, idx) => {
                const isCorrect = ans.is_correct === 1;
                const options = { 'A': ans.option_a, 'B': ans.option_b, 'C': ans.option_c, 'D': ans.option_d };

                return (
                  <div key={ans.id} style={{ marginBottom: 24, background: '#fff', padding: 20, borderRadius: 16, border: `1px solid ${C.g200}`, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 16, background: C.g100, color: C.g700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                          {idx + 1}
                        </div>
                        <div style={{ fontSize: 16, color: C.g900, fontWeight: 500, lineHeight: 1.5, marginTop: 4 }}>
                          {ans.question_text}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: isCorrect ? '#DCFCE7' : '#FEE2E2', color: isCorrect ? '#16A34A' : '#DC2626', fontWeight: 600, fontSize: 12 }}>
                        {isCorrect ? <><Check size={14} /> Correct</> : <><X size={14} /> Wrong</>}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: 10, paddingLeft: 44, marginBottom: 16 }}>
                      {Object.entries(options).map(([key, value]) => {
                        if (!value) return null;
                        const isSelected = ans.selected_option === key;
                        const isActualCorrect = ans.correct_answer === key;
                        
                        let bgColor = '#fff';
                        let borderColor = C.g200;
                        let textColor = C.g700;

                        if (isActualCorrect) {
                          bgColor = '#F0FDF4';
                          borderColor = '#4ADE80';
                          textColor = '#166534';
                        } else if (isSelected && !isActualCorrect) {
                          bgColor = '#FEF2F2';
                          borderColor = '#F87171';
                          textColor = '#991B1B';
                        }

                        return (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: bgColor, border: `2px solid ${borderColor}`, borderRadius: 10 }}>
                            <div style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${isSelected ? C.primary : C.g300}`, background: isSelected ? C.primary : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12 }}>
                              {isSelected && <div style={{ width: 10, height: 10, borderRadius: 5, background: '#fff' }} />}
                            </div>
                            <div style={{ fontSize: 15, color: textColor, fontWeight: (isActualCorrect || isSelected) ? 600 : 400 }}>{value}</div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
            
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.g100}`, background: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewDetail(null)} style={{ padding: '10px 20px', background: C.g100, color: C.g800, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {detailLoading && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
          <div style={{ padding: 24, background: '#fff', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12, fontWeight: 600, color: C.g800 }}>
            Loading details...
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ResultList;
